export type Vec3 = [number, number, number];

export interface FeatureBase {
  id: string;
  name: string;
  type: string;
  enabled: boolean;
  /** PBR material preset key (default = "default") */
  materialPreset?: string;
}

export interface BoxFeature extends FeatureBase {
  type: "box";
  width: number;
  height: number;
  depth: number;
  position: Vec3;
}

export interface CylinderFeature extends FeatureBase {
  type: "cylinder";
  radius: number;
  height: number;
  position: Vec3;
}

export interface SphereFeature extends FeatureBase {
  type: "sphere";
  radius: number;
  position: Vec3;
}

// Phase B
export const BOOLEAN_TYPES = ["fuse", "cut", "intersect"] as const;
export type BooleanType = (typeof BOOLEAN_TYPES)[number];

export interface BooleanFeature extends FeatureBase {
  type: BooleanType;
  targetRef: string;
  toolRef: string;
}

export function isBooleanFeature(f: Feature): f is BooleanFeature {
  return (BOOLEAN_TYPES as readonly string[]).includes(f.type);
}

// Phase C — Shape modifiers (single target ref + one parameter)
export const MODIFIER_TYPES = ["fillet", "chamfer", "shell", "draft"] as const;
export type ModifierType = (typeof MODIFIER_TYPES)[number];

export interface FilletFeature extends FeatureBase {
  type: "fillet";
  targetRef: string;
  radius: number;
}

export interface ChamferFeature extends FeatureBase {
  type: "chamfer";
  targetRef: string;
  distance: number;
}

export interface ShellFeature extends FeatureBase {
  type: "shell";
  targetRef: string;
  thickness: number;
}

export interface DraftFeature extends FeatureBase {
  type: "draft";
  targetRef: string;
  angle: number;
  axis: AxisName;
}

export type ModifierFeature = FilletFeature | ChamferFeature | ShellFeature | DraftFeature;

export function isModifierFeature(f: Feature): f is ModifierFeature {
  return (MODIFIER_TYPES as readonly string[]).includes(f.type);
}

/** Any feature that references other features (booleans + modifiers). */
export function hasTargetRef(f: Feature): f is Feature & { targetRef: string } {
  return "targetRef" in f;
}

// Phase D+E shared types
export type ProfileType = "rectangle" | "circle" | "rounded-rectangle";
export type AxisName = "X" | "Y" | "Z";
export type MirrorPlaneName = "XY" | "XZ" | "YZ";
export type SketchPlaneName = "XY" | "XZ" | "YZ";

// Phase G — Sketch
export type SketchCommand =
  | { tool: "moveTo"; point: [number, number] }
  | { tool: "lineTo"; point: [number, number] }
  | { tool: "arcTo"; point: [number, number]; sagitta: number }
  | { tool: "bezierTo"; point: [number, number]; cp1: [number, number]; cp2: [number, number] }
  | { tool: "splineTo"; point: [number, number] };

export interface SketchFeature extends FeatureBase {
  type: "sketch";
  commands: SketchCommand[];
  plane: SketchPlaneName;
  closed: boolean;
  operation: "extrude" | "revolve";
  depth: number;
  twistAngle?: number;
  taperFactor?: number;
  revolveAxis?: AxisName;
  revolveAngle?: number;
  position: Vec3;
}

export interface ExtrudeFeature extends FeatureBase {
  type: "extrude";
  profile: ProfileType;
  profileWidth?: number;
  profileHeight?: number;
  profileRadius?: number;
  depth: number;
  twistAngle?: number;
  taperFactor?: number;
  position: Vec3;
}

// Phase F — Loft
export interface LoftFeature extends FeatureBase {
  type: "loft";
  startProfile: ProfileType;
  startWidth: number;
  startHeight: number;
  startRadius: number;
  endProfile: ProfileType;
  endWidth: number;
  endHeight: number;
  endRadius: number;
  height: number;
  ruled: boolean;
  position: Vec3;
}

// Phase E — Revolve, Sweep, Mirror, Pattern
export interface RevolveFeature extends FeatureBase {
  type: "revolve";
  profile: ProfileType;
  profileWidth?: number;
  profileHeight?: number;
  profileRadius?: number;
  axis: AxisName;
  offset: number;
  angle: number;
  position: Vec3;
}

export interface SweepFeature extends FeatureBase {
  type: "sweep";
  profileRadius: number;
  helixRadius: number;
  pitch: number;
  turns: number;
  position: Vec3;
}

export const TRANSFORM_TYPES = ["mirror", "linearPattern", "circularPattern"] as const;
export type TransformType = (typeof TRANSFORM_TYPES)[number];

export interface MirrorFeature extends FeatureBase {
  type: "mirror";
  targetRef: string;
  plane: MirrorPlaneName;
}

export interface LinearPatternFeature extends FeatureBase {
  type: "linearPattern";
  targetRef: string;
  axis: AxisName;
  count: number;
  spacing: number;
}

export interface CircularPatternFeature extends FeatureBase {
  type: "circularPattern";
  targetRef: string;
  axis: AxisName;
  count: number;
  angle: number;
}

export type TransformFeature = MirrorFeature | LinearPatternFeature | CircularPatternFeature;

export function isTransformFeature(f: Feature): f is TransformFeature {
  return (TRANSFORM_TYPES as readonly string[]).includes(f.type);
}

// Phase I — Assembly
export const MATE_TYPES = ["faceToFace", "coincident", "fastened", "revolute", "slider"] as const;
export type MateType = (typeof MATE_TYPES)[number];

export interface MateConstraint {
  id: string;
  type: MateType;
  sourceFeatureId: string;
  targetFeatureId: string;
  sourcePoint: Vec3;
  sourceNormal: Vec3;
  targetPoint: Vec3;
  targetNormal: Vec3;
  /** Current angle in degrees (revolute mates) */
  constraintAngle?: number;
  /** Current offset in mm (slider mates) */
  constraintOffset?: number;
}

export interface ImportedPartFeature extends FeatureBase {
  type: "importedPart";
  importDocId: string;
  importDocName: string;
  importScript?: string;
  /** Pin to a specific version ID; when unset, always uses latest */
  importVersionId?: string;
  importPosition: Vec3;
  importRotation: Vec3;
  explosionOffset?: Vec3;
  mates?: MateConstraint[];
  /** Configuration parameters for the imported part (e.g. { len: 30 }) */
  configuration?: Record<string, number | string>;
}

// Phase L — Assembly Patterns
export interface AssemblyLinearPatternFeature extends FeatureBase {
  type: "assemblyLinearPattern";
  targetRef: string;
  axis: AxisName;
  count: number;
  spacing: number;
}

export interface AssemblyCircularPatternFeature extends FeatureBase {
  type: "assemblyCircularPattern";
  targetRef: string;
  axis: AxisName;
  count: number;
  angle: number;
}

export type AssemblyPatternFeature = AssemblyLinearPatternFeature | AssemblyCircularPatternFeature;

export function isAssemblyPatternFeature(f: Feature): f is AssemblyPatternFeature {
  return f.type === "assemblyLinearPattern" || f.type === "assemblyCircularPattern";
}

// Phase 5A — Sheet Metal
export interface SheetMetalBaseFeature extends FeatureBase {
  type: "sheetMetalBase";
  width: number;
  length: number;
  thickness: number;
  bendRadius: number;
  kFactor: number;
  position: Vec3;
}

export interface SheetMetalFlangeFeature extends FeatureBase {
  type: "sheetMetalFlange";
  targetRef: string;
  flangeLength: number;
  flangeAngle: number;   // degrees (90 = right angle bend)
  edge: "left" | "right" | "top" | "bottom";
}

export interface SheetMetalCornerReliefFeature extends FeatureBase {
  type: "sheetMetalCornerRelief";
  targetRef: string;
  reliefType: "circular" | "square" | "obround";
  reliefScale: number; // multiplier for auto-calculated size (default 1.0)
}

export type SheetMetalFeature = SheetMetalBaseFeature | SheetMetalFlangeFeature | SheetMetalCornerReliefFeature;

export function isSheetMetalFeature(f: Feature): f is SheetMetalFeature {
  return f.type === "sheetMetalBase" || f.type === "sheetMetalFlange" || f.type === "sheetMetalCornerRelief";
}

export type Feature =
  | BoxFeature
  | CylinderFeature
  | SphereFeature
  | BooleanFeature
  | FilletFeature
  | ChamferFeature
  | ShellFeature
  | DraftFeature
  | ExtrudeFeature
  | LoftFeature
  | RevolveFeature
  | SweepFeature
  | MirrorFeature
  | LinearPatternFeature
  | CircularPatternFeature
  | SketchFeature
  | ImportedPartFeature
  | AssemblyLinearPatternFeature
  | AssemblyCircularPatternFeature
  | SheetMetalBaseFeature
  | SheetMetalFlangeFeature
  | SheetMetalCornerReliefFeature;

export type PrimitiveFeature = BoxFeature | CylinderFeature | SphereFeature;
